import { AppSection } from './app-registry';

/**
 * Active-section resolution for the app masthead (SCRUM-593, masthead epic
 * story 6; rollout plan §3 "one tab is always active"). Pure: no router, no DI.
 *
 * Order, first match wins:
 * 1. `declared` — the section a page set on `MastheadContext`, if it names
 *    one of `sections`.
 * 2. URL prefix — for each section the candidates are `activeWhen`, or
 *    `[link]` when absent. A candidate matches when the path equals it or
 *    starts with it followed by `/` (a candidate that already ends with `/`
 *    is a plain prefix). The section with the LONGEST matching candidate wins,
 *    so `/apps/gemba/completed` beats `/apps/gemba`.
 * 3. `sections[0]` — a deep link to an unknown sub-page still lights one tab.
 *
 * `url` may carry a query string and/or fragment; both are ignored.
 *
 * @returns the active section's key, or `undefined` only when `sections` is empty.
 */
export function resolveActiveSection(
  sections: readonly AppSection[],
  url: string,
  declared: string | null = null,
): string | undefined {
  if (declared !== null && sections.some((section) => section.key === declared)) {
    return declared;
  }

  const path = stripQueryAndFragment(url);
  let best: { key: string; length: number } | undefined;
  for (const section of sections) {
    for (const candidate of section.activeWhen ?? [section.link]) {
      if (matchesPathPrefix(path, candidate) && (!best || candidate.length > best.length)) {
        best = { key: section.key, length: candidate.length };
      }
    }
  }
  return best?.key ?? sections[0]?.key;
}

/** `/a/b?x=1#top` → `/a/b`. */
export function stripQueryAndFragment(url: string): string {
  const end = Math.min(...['?', '#'].map((c) => url.indexOf(c)).filter((i) => i >= 0), url.length);
  return url.slice(0, end);
}

/**
 * Whole-segment prefix test shared by the masthead tabs and the sidebar
 * (`main-menu.model.ts`): `path` equals `candidate`, or starts with it followed
 * by `/` (a candidate that already ends with `/` is a plain prefix).
 */
export function matchesPathPrefix(path: string, candidate: string): boolean {
  if (path === candidate) {
    return true;
  }
  return path.startsWith(candidate.endsWith('/') ? candidate : `${candidate}/`);
}
